import { cn } from "@repo/design-system/utils";
import { ChevronsUpDown } from "lucide-react";
import type { ReactNode } from "react";

// Strips the Medusa Select trigger back to text + chevron so it can sit
// inside a dl row or a line item. The `!` importants are load-bearing: the
// trigger's own classes include `w-full h-8 px-2 bg-ui-bg-field
// shadow-buttons-neutral` and an already-important
// `data-[state=open]:!shadow-borders-*`, so same-strength overrides are the
// only way to win. Focus keeps a visible ring since the stock focus shadow is
// among what's stripped.
//
// Size, weight and colour are left to the host (pass them after this in `cn`,
// twMerge lets the later one win): a totals row and a line item read at
// different strengths.
export const inlineSelectTriggerClasses = cn(
  "!h-auto !w-auto !gap-1 !rounded !bg-transparent !p-0 !shadow-none",
  "hover:!bg-transparent hover:!text-purple-700",
  "data-[state=open]:!shadow-none",
  // The trigger is disabled while a change is in flight, and the stock
  // `disabled:!bg-ui-bg-disabled` would otherwise flash a filled grey pill
  // into the host. Text still greys via the stock disabled colour - that is
  // the wanted pending signal; the box is not.
  "disabled:!bg-transparent",
  "focus-visible:!shadow-none focus-visible:!ring-2 focus-visible:!ring-purple-500 focus-visible:!ring-offset-1",
  // Hide the trigger's own trailing chevron (a direct child of the button):
  // `InlineSelectValue` places its own, where the host wants it. Direct-child
  // selector so the replacement (nested in the value span) survives.
  "[&>svg]:hidden"
);

/**
 * Value/placeholder content for an inline trigger, carrying the chevron the
 * trigger's own is hidden for (see `inlineSelectTriggerClasses`).
 *
 * - `leading` (default): chevron first, text last, so a price stays
 *   right-aligned with the totals column it sits in.
 * - `trailing`: text first, chevron right after it, for text that reads as a
 *   line of copy.
 */
export function InlineSelectValue({
  children,
  chevron = "leading"
}: {
  children: ReactNode;
  chevron?: "leading" | "trailing";
}) {
  const icon = (
    <ChevronsUpDown
      className="h-3 w-3 flex-shrink-0 text-gray-400"
      aria-hidden="true"
    />
  );
  return (
    <span className="flex min-w-0 items-center gap-1">
      {chevron === "leading" && icon}
      <span className="truncate">{children}</span>
      {chevron === "trailing" && icon}
    </span>
  );
}
